import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  ExecutionStatus,
  getBulkActionJobData,
  JOB_TYPE_FIELD_POLICY,
  type JobExecutionStatus,
  type JobType,
  TERMINAL_EXECUTION_STATUSES,
} from '@cdw-ae/common-data';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { DateTimeService } from '@cdw-ae/date-time-service';
import * as JobsStatusActions from '@cdw-ae/jobs-status-state';
import { Store } from '@ngrx/store';
import { TailwindColors } from '../../../../../../common/constants/tailwind-colors';

interface JobDisplayItem {
  execution: JobExecutionStatus;
  progress?: number;
  statusText: string;
  statusColor: string;
  displayName: string;
}

const STATUS_CONFIG = {
  [ExecutionStatus.pending]: {
    text: 'Pending',
    color: TailwindColors.gray400,
    icon: 'sync',
    timePrefix: 'Created',
  },
  [ExecutionStatus.started]: {
    text: 'Running',
    color: TailwindColors.tertiaryBlueCta,
    icon: 'sync',
    timePrefix: 'Started',
  },
  [ExecutionStatus.finished]: {
    text: 'Completed',
    color: TailwindColors.tertiaryGreenDark,
    icon: 'check',
    timePrefix: 'Completed',
  },
  [ExecutionStatus.failed]: {
    text: 'Failed',
    color: TailwindColors.cdwRedPrimary,
    icon: 'close',
    timePrefix: 'Failed',
  },
  [ExecutionStatus.error]: {
    text: 'Error',
    color: TailwindColors.cdwRedPrimary,
    icon: 'close',
    timePrefix: 'Failed',
  },
  [ExecutionStatus.cancelledRunning]: {
    text: 'Cancelled',
    color: TailwindColors.gray400,
    icon: 'close',
    timePrefix: 'Cancelled',
  },
  [ExecutionStatus.cancelledPending]: {
    text: 'Cancelled',
    color: TailwindColors.gray400,
    icon: 'close',
    timePrefix: 'Cancelled',
  },
} as const;

const JOB_DETAIL_FIELD_MAP: Record<string, string> = {
  totalDeviceAssetsWritten: 'Device Assets',
  totalGroupingsAssetsWritten: 'Grouping Assets',
  totalUsersAssetsWritten: 'User Assets',
  totalRowsExported: 'Rows Exported',
  fileName: 'File',
};

/**
 * Fields in additionalJobData that should not be displayed as detail rows.
 * The skip counts are recorded for the audit log side panel (EDUS-2625).
 */
const HIDDEN_ADDITIONAL_DATA_FIELDS = new Set([
  'jobType',
  'format',
  'requestedCount',
  'skippedCount',
  'skippedByReason',
]);

/**
 * Render an additionalJobData / jobCounters field value as a string.
 * Auto-stringifies objects and arrays so we never show "[object Object]".
 * For fields needing custom formatting, hide them via JOB_TYPE_FIELD_POLICY
 * (or HIDDEN_ADDITIONAL_DATA_FIELDS) and render them as dedicated UI.
 */
function renderFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Derived from the shared list so the card can't disagree with the backend. */
const COMPLETED_STATUSES = new Set<ExecutionStatus>(
  TERMINAL_EXECUTION_STATUSES
);

@Component({
  selector: 'app-job-card',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressBarModule, CdwIconModule],
  templateUrl: './job-card.component.html',
  styleUrls: ['./job-card.component.scss'],
})
export class JobCardComponent {
  private readonly store = inject(Store);

  @Input() job!: JobDisplayItem;
  @Input() showCreator = false;

  get statusConfig() {
    return (
      STATUS_CONFIG[this.job.execution.executionStatus] ??
      STATUS_CONFIG[ExecutionStatus.pending]
    );
  }

  get isRunning(): boolean {
    return (
      this.job.execution.executionStatus === ExecutionStatus.started ||
      this.job.execution.executionStatus === ExecutionStatus.pending
    );
  }

  get isCompleted(): boolean {
    return COMPLETED_STATUSES.has(this.job.execution.executionStatus);
  }

  get hasFailed(): boolean {
    return (
      this.job.execution.executionStatus === ExecutionStatus.failed ||
      this.job.execution.executionStatus === ExecutionStatus.error
    );
  }

  get createdTime(): string {
    if (!this.job.execution.createdAt) return '';
    const timeAgo = DateTimeService.formatTimeAgo(this.job.execution.createdAt);
    return `Created ${timeAgo.toLowerCase()}`;
  }

  get completedTime(): string {
    if (!(this.isCompleted && this.job.execution.dt)) return '';
    const timeAgo = DateTimeService.formatTimeAgo(this.job.execution.dt);
    return `${this.statusConfig.timePrefix} ${timeAgo.toLowerCase()}`;
  }

  get relativeTime(): string {
    if (
      this.isCompleted &&
      this.job.execution.createdAt &&
      this.job.execution.dt
    ) {
      // Show both times for completed jobs
      return `${this.createdTime} • ${this.completedTime}`;
    }
    // For running/pending jobs, just show creation time
    return this.createdTime;
  }

  get jobDetails(): Array<{ label: string; value: string }> {
    const details: Array<{ label: string; value: string }> = [];
    const jobType = this.job.execution.additionalJobData?.jobType as
      | JobType
      | undefined;
    const perTypeHidden = new Set(
      (jobType && JOB_TYPE_FIELD_POLICY[jobType]?.hideFields) || []
    );

    // Read from jobCounters (atomic counters like asset counts)
    const jobCounters = this.job.execution.jobCounters;
    if (jobCounters) {
      Object.entries(jobCounters).forEach(([key, value]) => {
        const label = JOB_DETAIL_FIELD_MAP[key] || key;
        if (value != null) {
          details.push({ label, value: renderFieldValue(value) });
        }
      });
    }

    // Read from additionalJobData (export metadata like totalRowsExported)
    const additionalData = this.job.execution.additionalJobData;
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        if (HIDDEN_ADDITIONAL_DATA_FIELDS.has(key)) return;
        if (perTypeHidden.has(key)) return;
        if (key === 'outputReference') return; // displayed as link, not detail row
        const label = JOB_DETAIL_FIELD_MAP[key] || key;
        if (value != null) {
          details.push({ label, value: renderFieldValue(value) });
        }
      });
    }

    return details;
  }

  get hasExportLink(): boolean {
    if (!this.isCompleted) return false;
    const ref = this.job.execution.additionalJobData?.outputReference;
    return typeof ref === 'string' && ref.length > 0;
  }

  get exportLinkLabel(): string {
    const format = this.job.execution.additionalJobData?.format as string;
    return format === 'google-sheets'
      ? 'View in Google Sheets'
      : 'Download CSV';
  }

  downloadExport(): void {
    this.store.dispatch(
      JobsStatusActions.downloadExport({
        jobId: this.job.execution.jobId,
        customerId: this.job.execution.customerId,
      })
    );
  }

  /**
   * Cancel button visibility — true while the job is in flight (started or
   * pending). Driven by the same `isRunning` getter the existing UI already
   * uses for progress-bar visibility, so the surface stays self-consistent.
   * Hidden for terminal-state jobs (cancel of a finished job is a no-op).
   */
  get canCancel(): boolean {
    return this.isRunning;
  }

  /**
   * Cancel a running job. Dispatches the same `cancelJobRequested` action
   * the device-search stuck-job banner uses — the cancelJob$ effect handles
   * the optimistic local lock release (selectionLockReleaseRequested
   * emitted FIRST, spinners + lock clear instantly) and then the HTTP
   * cancel call.
   *
   * gridSessionUuid comes from the job's additionalJobData (set at enqueue
   * time by saas-device-api). For non-bulk jobs (e.g., a cache refresh
   * triggered from the navbar without a grid context), it'll be undefined,
   * which the cancelJob$ effect handles gracefully (skips the optimistic
   * lock release; just fires the API call).
   */
  cancelJob(): void {
    const { gridSessionUuid } = getBulkActionJobData(
      this.job.execution.additionalJobData
    );
    this.store.dispatch(
      JobsStatusActions.cancelJobRequested({
        jobId: this.job.execution.jobId,
        customerId: this.job.execution.customerId,
        gridSessionUuid,
      })
    );
  }
}
